import React, {useContext, useState} from 'react';
import {observer} from 'mobx-react-lite';
import './Login.css'
import logo from '../../assets/logo.png'
import Button from "../../components/common/Button/Button";
import Input from "../../components/common/Input/Input";
import TotpCodeInput from "../../components/common/TotpCodeInput/TotpCodeInput";
import TotpQr from "../../components/common/TotpQr/TotpQr";
import Footer from "../../components/Footer/Footer";
import {Context} from "../../main";
import {useNotification} from "../../hooks/useNotification";
import {generateIcon, iconMap, siteName} from "../../utils/constants";
import { useCommunitySettings } from "../../context/CommunitySettingsContext";

const usernameIcon = generateIcon(iconMap.profile, "input-icon")
const passwordIcon = generateIcon(iconMap.password, "input-icon")
const styleInput = {width: '300px'}

const formatSecret = (secret) => String(secret || '').replace(/(.{4})/g, '$1 ').trim()

const LoginPage = () => {
    const {store} = useContext(Context)
    const notification = useNotification()
    const { settings } = useCommunitySettings();
    const [step, setStep] = useState('credentials')
    const [totp, setTotp] = useState({
        token: '',
        secret: '',
        otpauthUrl: '',
        code: '',
    })
    const [state, setState] = useState({
        username: '',
        password: '',
    })

    const showAuthError = () => {
        if (store.isError) notification({
            type: "warning",
            title: "Помилка авторизації",
            message: store.errorMessage,
            placement: "top"
        });
    }

    const onChangeHandler = (name, value) => {
        if (name === 'username') {
            value = value.replaceAll(' ', '')
        }
        setState(prevState => ({
            ...prevState,
            [name]: value,
        }))
    }

    const onCodeChange = (value) => {
        setTotp(prev => ({ ...prev, code: value }))
    }

    const resetToCredentials = () => {
        setStep('credentials')
        setTotp({ token: '', secret: '', otpauthUrl: '', code: '' })
        setState({ username: '', password: '' })
    }

    const copySecret = async () => {
        try {
            await navigator.clipboard.writeText(totp.secret)
            notification({
                type: "success",
                title: "Ключ скопійовано",
                message: "Вставте його в Google Authenticator",
                placement: "top",
                duration: 2,
            })
        } catch {
            notification({
                type: "warning",
                title: "Не вдалося скопіювати",
                message: "Скопіюйте ключ вручну",
                placement: "top",
            })
        }
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        if (state.username && state.password) {
            const result = await store.login(state.username, state.password)
            if (store.isError) {
                showAuthError()
                return
            }
            if (result?.requires2faSetup) {
                setTotp({
                    token: result.totpToken,
                    secret: result.secret,
                    otpauthUrl: result.otpauthUrl,
                    code: '',
                })
                setStep('setup')
                return
            }
            if (result?.requires2fa) {
                setTotp({
                    token: result.totpToken,
                    secret: '',
                    otpauthUrl: '',
                    code: '',
                })
                setStep('challenge')
            }
        }
    }

    const onConfirmSetup = async (e) => {
        e.preventDefault()
        if (totp.code.length !== 6) return
        const result = await store.confirmTotpSetup(totp.token, totp.code)
        if (store.isError || !result) {
            showAuthError()
        }
    }

    const onConfirmCode = async (e) => {
        e.preventDefault()
        if (totp.code.length !== 6) return
        const result = await store.loginTotp(totp.token, totp.code)
        if (store.isError || !result) {
            showAuthError()
        }
    }

    const codeField = (
        <TotpCodeInput
            value={totp.code}
            onChange={onCodeChange}
            disabled={store.isLoading}
        />
    )

    return (
        <React.Fragment>
            <main className="main">
                <div className="main__container">
                    <div className="main__inner">
                        <div className="page-login">
                            <div className={"page-login__logo"}>
                                <img src={logo} alt={settings?.altCityName || ''} style={{height:"110px", width:"auto"}}/>
                            </div>
                            <h3 className="title title--sm title--mb-20">
                                {step === 'credentials' ? siteName : 'Двофакторна перевірка'}
                            </h3>
                            {step === 'credentials' && (
                            <form onSubmit={onSubmit} className="page-login__form" autoComplete="off">
                                <Input
                                    autoComplete="username"
                                    style={styleInput}
                                    icon={usernameIcon}
                                    value={state.username}
                                    type="text"
                                    name="username"
                                    placeholder="Логін"
                                    required
                                    onChange={onChangeHandler}
                                />
                                <Input
                                    style={styleInput}
                                    autoComplete="current-password"
                                    icon={passwordIcon}
                                    type="password"
                                    value={state.password}
                                    name="password"
                                    placeholder="Пароль"
                                    required
                                    onChange={onChangeHandler}
                                />
                                <Button type="submit" disabled={store.isLoading}>
                                    Вхід
                                </Button>
                            </form>
                            )}
                            {step === 'setup' && (
                            <form onSubmit={onConfirmSetup} className="page-login__form" autoComplete="off">
                                <p className="page-login__hint">
                                    1. Завантажте Google Authenticator з Google Play або App Store.
                                </p>
                                <p className="page-login__hint">
                                    2. У додатку оберіть введення ключа вручну і вставте цей ключ:
                                </p>
                                <code className="page-login__secret">{formatSecret(totp.secret)}</code>
                                <Button type="button" onClick={copySecret}>
                                    Копіювати ключ
                                </Button>
                                <TotpQr value={totp.otpauthUrl}/>
                                <p className="page-login__hint page-login__hint--muted">
                                    Цей ключ — на цей логін. Якщо логіном користується весь відділ, код буде спільний.
                                </p>
                                <p className="page-login__hint">
                                    3. Введіть 6 цифр з додатка:
                                </p>
                                {codeField}
                                <Button type="submit" disabled={store.isLoading || totp.code.length !== 6}>
                                    Підтвердити
                                </Button>
                                <Button type="button" onClick={resetToCredentials}>
                                    Назад
                                </Button>
                            </form>
                            )}
                            {step === 'challenge' && (
                            <form onSubmit={onConfirmCode} className="page-login__form" autoComplete="off">
                                <p className="page-login__hint">
                                    Введіть код із Google Authenticator.
                                </p>
                                {codeField}
                                <Button type="submit" disabled={store.isLoading || totp.code.length !== 6}>
                                    Продовжити
                                </Button>
                                <Button type="button" onClick={resetToCredentials}>
                                    Назад
                                </Button>
                            </form>
                            )}
                        </div>
                    </div>
                </div>
            </main>
            <Footer year={2024}/>
        </React.Fragment>
    );
};
const Login = observer(LoginPage);
export default Login;
